import { RbacSettingWithPath } from 'components/containers/settings/roles-and-permissions/ConfigureRoleBlade'
import { RbacSettingList } from 'gen/rtk-openapi'
/**
 * Transforms the API response model for RBAC settings into a flat structure
 * that is compatible with MUI's `DataGridPro`.
 *
 * @param {RbacSettingList} permissions - The list of RBAC settings returned from the API.
 * @returns {RbacSettingWithPath[]} - A flattened list where each entry contains its full path.
 *
 * The transformation ensures:
 * - Each item includes a `path` property, which represents its hierarchical location.
 * - Nested permissions are expanded recursively into a single-level array.
 */
export const mapApiModelToGridModel = (permissions: RbacSettingList): RbacSettingWithPath[] => {
  const result: RbacSettingWithPath[] = []
  const traverse = (items: RbacSettingList, parentPath: string[] = []): void => {
    items.forEach(item => {
      const someDenied = item.children?.some(child => child.allow == false)
      const someAllowed = item.children?.some(child => child.allow == true)
      const newItem = {
        ...item,
        path: [...parentPath, item.displayName], // DataGridPro requires a path for tree view
        indeterminate: someDenied && someAllowed, // Indeterminate state for checkboxes
      }
      result.push(newItem)
      if (item.children && item.children.length > 0) {
        traverse(item.children, [...parentPath, item.displayName]) // Recursively process children
      }
    })
  }
  traverse(permissions)
  return result
}

/**
 * Converts a flattened DataGrid model back into the nested structure expected by the API.
 *
 * @param {RbacSettingWithPath[]} flatPermissions - The flattened permissions list.
 * @returns {RbacSettingList} - A hierarchical RBAC structure for API submission.
 *
 * The function works by:
 * - Storing all permissions in a Map for quick lookups.
 * - Reconstructing parent-child relationships based on the `path` array.
 * - Returning only the top-level permissions (modules) that contain nested children.
 */
export const mapGridModelToApiModel = (flatPermissions: RbacSettingWithPath[]): RbacSettingList => {
  const map = new Map()
  // Initialize all entries in a Map for fast lookups
  flatPermissions.forEach(perm => {
    const { path, ...rest } = perm // Remove path before storing
    map.set(perm.id, { ...rest, children: [] }) // Store without path
  })

  // Rebuild the hierarchy
  flatPermissions.forEach(perm => {
    if (perm.path.length > 1) {
      const parentPath = perm.path.slice(0, -1)
      const parent = flatPermissions.find(p => JSON.stringify(p.path) === JSON.stringify(parentPath))
      if (parent) {
        map.get(parent.id).children.push(map.get(perm.id)) // Assign child to parent
      }
    }
  })

  // Return only top-level modules
  return Array.from(map.values()).filter(perm => perm.id.includes('root'))
}

/**
 * Handles automatic checking and unchecking of checkboxes in the RBAC settings grid.
 *
 * @param {RbacSettingWithPath[]} prevRows - The current state of the RBAC settings grid.
 * @param {string} id - The ID of the row being updated.
 * @param {boolean} checked - Whether the checkbox was checked or unchecked.
 * @returns {RbacSettingWithPath[]} - The updated state of the RBAC grid.
 *
 * The function ensures:
 * - When a child is checked, its parent is automatically checked.
 * - When all children of a parent are unchecked, the parent is also unchecked.
 * - When a parent is unchecked, all of its children are also unchecked.
 */
export const rowUpdateFunction = (prevRows: RbacSettingWithPath[], id: string, checked: boolean) => {
  const updatedRows = prevRows.map(row => (row.id === id ? { ...row, allow: checked } : row))

  // Find the changed row (either a parent or a child)
  const changedRow = updatedRows.find(row => row.id === id)
  if (!changedRow || changedRow.path.length < 1) return updatedRows

  const parentPath = changedRow.path.slice(0, -1)
  const parentRow = updatedRows.find(row => JSON.stringify(row.path) === JSON.stringify(parentPath))
  // handle ROOT MODULE change when parent unchecked, uncheck all children and vice versa
  if (!parentRow) {
    if (!checked) {
      // all children should be unchecked
      const children = updatedRows.filter(row => row.path[0] === changedRow.displayName)
      return updatedRows.map(row =>
        children.some(child => child.id === row.id) ? { ...row, allow: false, indeterminate: false } : row,
      )
    } else {
      // all children should be checked
      const children = updatedRows.filter(row => row.path[0] === changedRow.displayName)
      return updatedRows.map(row => (children.some(child => child.id === row.id) ? { ...row, allow: true } : row))
    }
  }
  if (parentRow) {
    // Get all children of the parent
    const children = updatedRows.filter(
      row =>
        row.path.length === parentPath.length + 1 &&
        row.path.slice(0, -1).every((value, index) => value === parentPath[index]),
    )
    // update parent checkbox style and status based on changes to children
    const anyChecked = children.some(child => child.allow === true)
    const anyUnChecked = children?.some(child => child.allow === false)
    let updatedRowsWithParent = updatedRows.map(row =>
      row.id === parentRow.id ? { ...row, allow: anyChecked, indeterminate: anyChecked && anyUnChecked } : row,
    )
    // If the parent is unchecked, uncheck all children
    if (!checked && changedRow.id === parentRow.id) {
      updatedRowsWithParent = updatedRowsWithParent.map(row =>
        children.some(child => child.id === row.id) ? { ...row, allow: false } : row,
      )
    }
    return updatedRowsWithParent
  }
  return updatedRows
}
